import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type {
  ScanServiceType,
  SelectedFiles,
  SelectedFileType,
  SelectedTeeth,
  UnisonProjectTypeId,
} from '@witmetrics/utils';
import { formatUserName } from '@witmetrics/utils/strings';
import {
  asyncMap,
  filterOutKeyValue,
  filterOutKeyValues,
} from '@witmetrics/utils/arrays';
import { generateUuid } from '@witmetrics/utils/uuid';
import { useLead } from '@witmetrics/data-store';
import { useToggle } from '@/hooks/useToggle';
import { useId } from '@/hooks/useId';
import { useString } from '@/hooks/useString';
import { buildSelectedFile } from '@/utils/files';
import type {
  NewCaseValue,
  PendingSubmission,
  PendingTask,
  PendingTaskData,
} from './types';
import {
  DEFAULT_SELECTED_TEETH,
  RELU_LOWER_ARCH,
  RELU_UPPER_ARCH,
} from './constants';
import { buildInitialFiles, buildInitialParams } from './utils';

export type { PendingSubmission, PendingTask, PendingTaskData };

export interface NewCaseProviderProps extends PropsWithChildren {
  projectTypeId: UnisonProjectTypeId;
  serviceType?: ScanServiceType;
}

const NewCaseContext = createContext<NewCaseValue | undefined>(undefined);

export default function NewCaseProvider({
  projectTypeId,
  serviceType,
  children,
}: NewCaseProviderProps) {
  // NOTE: using a ref to handle stale state merging
  const selectedFilesRef = useRef<SelectedFiles>(buildInitialFiles());
  const externalRefRef = useRef(`${Date.now()}`); // For resetting to initial value
  const [leadId, setLeadId] = useId();
  const lead = useLead(leadId);
  const [name, setName] = useString();
  const [externalRef, setExternalRef] = useString(externalRefRef.current);
  const [notes, setNotes] = useString();
  const [params, setParams] = useState(buildInitialParams(serviceType));
  const [selectedTeeth, setSelectedTeeth] = useState<SelectedTeeth>({
    ...DEFAULT_SELECTED_TEETH,
  });
  const [selectedFiles, setSelectedFiles] =
    useState<SelectedFiles>(buildInitialFiles());
  const [pendingSubmissions, setPendingSubmissions] = useState<
    PendingSubmission[]
  >([]);
  const [pendingTasks, setPendingTasks] = useState<PendingTask[]>([]);
  const [isPreVeneers, togglePreVeneers] = useToggle();

  useEffect(() => {
    handleSetNameFromLead();
  }, [lead?.id]);

  const handleSetNameFromLead = () => {
    if (lead) setName(formatUserName(lead));
  };

  const handleSelectFiles: NewCaseValue['onSelectFiles'] = async (
    fileType,
    files
  ) => {
    const result = { ...selectedFilesRef.current };
    const newFiles = await asyncMap(files, buildSelectedFile);
    if (fileType === RELU_UPPER_ARCH || fileType === RELU_LOWER_ARCH) {
      // Only allow one RELU file per type
      result[fileType] = newFiles;
    } else {
      result[fileType] = [...result[fileType], ...newFiles];
    }
    selectedFilesRef.current = result;
    setSelectedFiles(result);
  };

  const handleClearFiles: NewCaseValue['onClearFiles'] = (removedTypes) => {
    const result = { ...selectedFilesRef.current };
    removedTypes.forEach((key) => {
      result[key] = [];
    });
    selectedFilesRef.current = result;
    setSelectedFiles(result);
  };

  const handleChangeFile: NewCaseValue['onChangeFile'] = (fileType, file) => {
    const result = { ...selectedFilesRef.current };
    result[fileType] = result[fileType].map((sf) =>
      sf.uuid === file.uuid ? file : sf
    );
    selectedFilesRef.current = result;
    setSelectedFiles(result);
  };

  const handleRemoveFiles: NewCaseValue['onRemoveFiles'] = (uuids) => {
    const result = { ...selectedFilesRef.current };
    Object.entries(result).forEach(([key, value]) => {
      result[key as SelectedFileType] = filterOutKeyValues(
        value,
        'uuid',
        uuids
      );
    });
    selectedFilesRef.current = result;
    setSelectedFiles(result);
  };

  const handleAddPendingSubmission: NewCaseValue['onAddPendingSubmission'] = ({
    formId,
    versionId,
    answers,
  }) => {
    const uuid = generateUuid();
    setPendingSubmissions((prev) => [
      ...prev,
      { uuid, formId, versionId, answers },
    ]);
    return uuid;
  };

  const handleUpdatePendingSubmission: NewCaseValue['onUpdatePendingSubmission'] =
    (uuid, { formId, versionId, answers }) => {
      setPendingSubmissions((prev) =>
        prev.map((s) =>
          s.uuid === uuid ? { uuid, formId, versionId, answers } : s
        )
      );
    };

  const handleRemovePendingSubmission: NewCaseValue['onRemovePendingSubmission'] =
    (uuid) => {
      setPendingSubmissions((prev) => filterOutKeyValue(prev, 'uuid', uuid));
    };

  const handleAddPendingTask: NewCaseValue['onAddPendingTask'] = ({
    dueAt,
    assignedTo,
    description,
  }) => {
    const uuid = generateUuid();
    setPendingTasks((prev) => [
      ...prev,
      { uuid, dueAt, assignedTo, description },
    ]);
    return uuid;
  };

  const handleUpdatePendingTask: NewCaseValue['onUpdatePendingTask'] = (
    uuid,
    { dueAt, assignedTo, description }
  ) => {
    setPendingTasks((prev) =>
      prev.map((t) =>
        t.uuid === uuid ? { uuid, dueAt, assignedTo, description } : t
      )
    );
  };

  const handleRemovePendingTask: NewCaseValue['onRemovePendingTask'] = (
    uuid
  ) => {
    setPendingTasks((prev) => filterOutKeyValue(prev, 'uuid', uuid));
  };

  const contextValue: NewCaseValue = {
    projectTypeId,
    serviceType,
    leadId,
    name,
    externalRef,
    notes,
    params,
    selectedTeeth,
    selectedFiles,
    pendingSubmissions,
    pendingTasks,
    isPreVeneers,
    onSelectFiles: handleSelectFiles,
    onClearFiles: handleClearFiles,
    onChangeFile: handleChangeFile,
    onRemoveFiles: handleRemoveFiles,
    onSelectLead: setLeadId,
    onUpdateName: setName,
    onResetName: handleSetNameFromLead,
    onUpdateExternalRef: setExternalRef,
    onResetExternalRef: () => setExternalRef(externalRefRef.current),
    onUpdateNotes: setNotes,
    onUpdateParams: setParams,
    onUpdateSelectedTeeth: setSelectedTeeth,
    onUpdateSelectedFiles: setSelectedFiles,
    onAddPendingSubmission: handleAddPendingSubmission,
    onUpdatePendingSubmission: handleUpdatePendingSubmission,
    onRemovePendingSubmission: handleRemovePendingSubmission,
    onAddPendingTask: handleAddPendingTask,
    onUpdatePendingTask: handleUpdatePendingTask,
    onRemovePendingTask: handleRemovePendingTask,
    onTogglePreVeneers: togglePreVeneers,
  };

  return (
    <NewCaseContext.Provider value={contextValue}>
      {children}
    </NewCaseContext.Provider>
  );
}

export function useNewCase() {
  const context = useContext(NewCaseContext);
  if (!context) {
    throw Error('useNewCase must be used within the NewCaseProvider');
  }
  return context;
}
